import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import {
  useItem,
  useItemAttributes,
  useItemBrands,
  useItemModels,
  useItemSections,
  useItemSizes,
  useUpdateItemAttribute,
} from '@/hooks/item';
import { UpdateItemAttributeModal } from '@/types/modal';
import { AutoCompleteDropdown, Dropdown } from '@/components/molecules/common/Form';
import { ErrorModal, NoFooterModal, StandardModal } from '@/components/molecules/common/Modal';
import styles from './Modal.module.scss';

const View: React.FC<{ modal: UpdateItemAttributeModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  // Single value
  const initialBrand = {
    name: item.brandName,
    id: item.brandId,
  };
  const [brand, setBrand] = useState(initialBrand);

  const initialSection = {
    name: item.sectionName,
    id: item.sectionId,
  };
  const [section, setSection] = useState(initialSection);

  const initialModel = {
    name: item.model,
    id: item.modelId,
  };
  const [model, setModel] = useState(initialModel);

  const initialMaterial = {
    name: item.material,
    id: item.materialId,
  };
  const [material, setMaterial] = useState(initialMaterial);

  const initialColor = {
    name: item.color,
    id: item.colorId,
  };
  const [color, setColor] = useState(initialColor);

  const initialCondition = {
    name: item.condition,
    id: item.conditionId,
  };
  const [condition, setCondition] = useState(initialCondition);

  const emptySize = '--';
  const [size, setSize] = useState(item.size ? item.size : emptySize);

  // Default value
  const emptyModel = {
    name: '--',
    id: 0,
  };

  // Other state
  const [waiting, setWaiting] = useState(false);
  const [brandSearch, setBrandSearch] = useState(item.brandName);

  const sectionR = useItemSections();
  const brandR = useItemBrands(item.brandId);
  const modelR = useItemModels(brand.id, section.id);
  const materialR = useItemAttributes('materials', item.productId, item.materialId);
  const sizeR = useItemSizes('sizes', item.productId, section.id);
  const colorR = useItemAttributes('colors', item.productId, item.colorId);
  const conditionR = useItemAttributes('conditions', item.productId, item.conditionId);

  const sectionList = sectionR.kind === 'Loaded' ? sectionR.data.data : [];
  const brandList = brandR.kind === 'Loaded' ? brandR.data.data : [];
  const modelList = modelR.kind === 'Loaded' ? [emptyModel, ...modelR.data.data] : [emptyModel];
  const materialList = materialR.kind === 'Loaded' ? materialR.data.data : [];
  const sizeList = sizeR.kind === 'Loaded' ? [emptySize, ...sizeR.data.data] : [emptySize];
  const colorList = colorR.kind === 'Loaded' ? colorR.data.data : [];
  const conditionList = conditionR.kind === 'Loaded' ? conditionR.data.data : [];

  const [sizes, setSizes] = useState(sizeList);

  const itemR = useItem(item.productId, false);
  const submitR = useUpdateItemAttribute(item.productId);

  useEffect(() => {
    if (sizeR.kind === 'Loaded') {
      const sizeList = sizeR.kind === 'Loaded' ? [emptySize, ...sizeR.data.data] : [emptySize];
      setSizes(sizeList);
    }
  }, [sizeR.kind]);

  useEffect(() => {
    sizeR.refetch();
    const sizeList = sizeR.kind === 'Loaded' ? [emptySize, ...sizeR.data.data] : [emptySize];
    setSizes(sizeList);
    const item = sizeList.find((item) => item === size);
    if (item) {
      setSize(size);
    }
  }, [section]);

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      setWaiting(true);
      setTimeout(() => {
        onClose();
        itemR.refetch();
      }, 7000);
    }
  }, [submitR.kind]);

  if (waiting) {
    return (
      <NoFooterModal
        title={t('modal.update_item_attribute.title')}
        body={
          <>
            <Skeleton
              height={20}
              variant="rounded"
            />
            <div className={styles.updateItemAttributeWait}>{t('modal.update_item_attribute.waiting')}</div>
          </>
        }
        onClose={onClose}
        modalName="update_item_attribute"
      />
    );
  }

  if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.update_item_attribute.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  }

  const body = (
    <div className={styles.updateItemAttribute}>
      <div className={styles.text1rem}>{`${t('modal.update_item_attribute.item')} #${item.productId}`}</div>
      <div className={styles.updateItemAttributeFields}>
        <Dropdown
          required={true}
          disabled={sectionR.kind === 'Called'}
          label={t('modal.update_item_attribute.section')}
          value={String(section.id)}
          onSelect={(v) => {
            const item = sectionList.find((item) => item.id === Number(v));
            if (item) {
              setSection(item);
            }
          }}
          items={sectionList.map(({ id, name }) => ({ label: name, value: String(id) }))}
        />
        <Dropdown
          required={true}
          disabled={materialR.kind === 'Called'}
          label={t('modal.update_item_attribute.material')}
          value={String(material.id)}
          onSelect={(v) => {
            const item = materialList.find((item) => item.id === Number(v));
            if (item) {
              setMaterial(item);
            }
          }}
          items={materialList.map(({ id, name }) => ({ label: name, value: String(id) }))}
        />
      </div>
      <div className={styles.updateItemAttributeFields}>
        <div className={styles.updateItemAttributeField}>
          <AutoCompleteDropdown
            required={true}
            disabled={brandR.kind === 'Called'}
            label={t('modal.update_item_attribute.brand')}
            helperText={t('modal.update_item_attribute.note')}
            data={brandList.map(({ id, name }) => ({ label: name, value: String(id) }))}
            inputValue={brandSearch}
            onInputChange={(v: string) => {
              setBrandSearch(v);
              setModel(emptyModel);
            }}
            onChange={(label) => {
              const item = brandList.find(({ name }) => label === name);
              if (item) {
                setBrandSearch(item.name);
                setBrand(item);
              }
            }}
          />
        </div>
        <div className={styles.updateItemAttributeField}>
          <Dropdown
            required={true}
            disabled={colorR.kind === 'Called'}
            label={t('modal.update_item_attribute.color')}
            value={String(color.id)}
            onSelect={(v) => {
              const item = colorList.find((item) => item.id === Number(v));
              if (item) {
                setColor(item);
              }
            }}
            items={colorList.map(({ id, name }) => ({ label: name, value: String(id) }))}
          />
        </div>
      </div>
      <div className={styles.updateItemAttributeFields}>
        <Dropdown
          required={false}
          disabled={modelR.kind === 'Called'}
          label={t('modal.update_item_attribute.model')}
          value={String(model.id)}
          onSelect={(v) => {
            const item = modelList.find((item) => item.id === Number(v));
            if (item) {
              setModel(item);
            }
          }}
          items={modelList.map(({ id, name }) => ({ label: name, value: String(id) }))}
        />
        <Dropdown
          required={false}
          disabled={sizeR.kind === 'Called'}
          label={t('modal.update_item_attribute.size')}
          value={size}
          onSelect={(v) => {
            const item = sizes.find((item) => item === v);
            if (item) {
              setSize(item);
            }
          }}
          items={sizes.map((item) => ({ label: item, value: item }))}
        />
      </div>
      <div className={styles.updateItemAttributeFields}>
        <div className={styles.updateItemAttributeField}>
          <Dropdown
            required={true}
            disabled={conditionR.kind === 'Called'}
            label={t('modal.update_item_attribute.condition')}
            value={String(condition.id)}
            onSelect={(v) => {
              const item = conditionList.find((item) => item.id === Number(v));
              if (item) {
                setCondition(item);
              }
            }}
            items={conditionList.map(({ id, name }) => ({ label: name, value: String(id) }))}
          />
        </div>
        <div className={styles.updateItemAttributeField} />
      </div>
    </div>
  );

  const canUpdate =
    submitR.kind !== 'Loading' &&
    (initialBrand.id !== brand.id ||
      initialSection.id !== section.id ||
      initialModel.id !== model.id ||
      initialMaterial.id !== material.id ||
      initialColor.id !== color.id ||
      '' !== size ||
      initialCondition.id !== condition.id);

  const onSubmit = () => {
    const lastModel = modelList.find((item) => item.id === Number(model.id));
    const lastSize = sizes.find((item) => item === size);
    submitR.mutate({
      brandId: brand.id,
      sectionId: section.id,
      modelId: lastModel?.id ? lastModel.id : 0,
      size: lastSize ? (lastSize !== emptySize ? lastSize : '') : '',
      materialId: material.id,
      colorId: color.id,
      conditionId: condition.id,
    });
  };

  return (
    <StandardModal
      title={t('modal.update_item_attribute.title')}
      onClose={onClose}
      onSubmit={onSubmit}
      submittable={canUpdate}
      body={body}
      containerStyle={{ width: 600 }}
      modalName="update_item_attribute"
    />
  );
};

export default View;
